import React, { useState, useEffect } from 'react';
import { FileText, Upload, Clock, CheckCircle, AlertTriangle, Calendar, User, Loader2, X, Paperclip, MessageSquare, Download } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { celebrate } from '../lib/celebrate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssignmentsPage = () => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submissionNotes, setSubmissionNotes] = useState('');
  const [submissionFile, setSubmissionFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      toast.error('Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/assignments/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissionFile({ name: file.name, url: data.file_url });
        toast.success('File uploaded successfully');
      } else {
        toast.error('Failed to upload file');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedAssignment) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/assignments/${selectedAssignment.id}/submit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          notes: submissionNotes,
          file_url: submissionFile?.url || null
        })
      });

      if (response.ok) {
        celebrate(0.6);
        toast.success('Assignment submitted successfully!');
        setShowSubmitModal(false);
        setSelectedAssignment(null);
        setSubmissionNotes('');
        setSubmissionFile(null);
        fetchAssignments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit assignment');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const openSubmitModal = (assignment) => {
    setSelectedAssignment(assignment);
    setSubmissionNotes('');
    setSubmissionFile(null);
    setShowSubmitModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'submitted': return <Clock className="w-5 h-5 text-blue-500" />;
      case 'overdue': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default: return <FileText className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      submitted: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      overdue: 'bg-red-100 text-red-700'
    };
    return styles[status] || styles.pending;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Download assignment file with authentication
  const handleDownloadAssignment = async (fileUrl, fileName) => {
    try {
      const token = localStorage.getItem('access_token');
      let downloadUrl = fileUrl;
      
      if (fileUrl.startsWith('/api')) {
        downloadUrl = `${API_URL}${fileUrl}`;
      }
      
      // Add token
      const separator = downloadUrl.includes('?') ? '&' : '?';
      downloadUrl = `${downloadUrl}${separator}token=${token}`;
      
      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'assignment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('File downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const filteredAssignments = assignments.filter(a => {
    if (filter === 'all') return true;
    return a.status === filter;
  });

  const stats = {
    total: assignments.length,
    pending: assignments.filter(a => a.status === 'pending').length,
    submitted: assignments.filter(a => a.status === 'submitted').length,
    completed: assignments.filter(a => a.status === 'completed').length,
    overdue: assignments.filter(a => a.status === 'overdue').length
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16 pb-8" data-testid="assignments-page">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">My Assignments</h1>
          <p className="text-sm lg:text-base text-gray-600">View and submit your homework assignments</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4 mb-6 lg:mb-8">
          {[
            { label: 'Total', value: stats.total, color: 'bg-gray-100' },
            { label: 'Pending', value: stats.pending, color: 'bg-yellow-100' },
            { label: 'Submitted', value: stats.submitted, color: 'bg-blue-100' },
            { label: 'Completed', value: stats.completed, color: 'bg-green-100' },
            { label: 'Overdue', value: stats.overdue, color: 'bg-red-100' }
          ].map(stat => (
            <div key={stat.label} className={`${stat.color} rounded-xl p-3 lg:p-4 text-center`}>
              <div className="text-xl lg:text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-xs lg:text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'pending', 'submitted', 'completed', 'overdue'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 lg:px-4 py-2 rounded-full text-xs lg:text-sm font-medium transition-colors whitespace-nowrap ${
                filter === f
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Assignments List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : filteredAssignments.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 lg:py-20 bg-white rounded-xl border"
          >
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-block"
            >
              <FileText className="w-16 h-16 mx-auto text-purple-200 mb-4" />
            </motion.div>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              {filter === 'all' ? "You're all caught up!" : `No ${filter} assignments`}
            </h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              {filter === 'all'
                ? "No homework yet — enjoy this quiet moment, or get a head start in the Library."
                : `Switch tabs to see other assignments.`}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredAssignments.map((assignment, idx) => (
              <motion.div
                key={assignment.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(idx * 0.05, 0.4), duration: 0.25 }}
                whileHover={{ y: -2 }}
                className="bg-white rounded-xl border p-4 lg:p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                  <div className="flex items-start gap-3 lg:gap-4">
                    <div className={`p-2 lg:p-3 rounded-full flex-shrink-0 ${getStatusBadge(assignment.status).split(' ')[0]}`}>
                      {getStatusIcon(assignment.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base lg:text-lg font-semibold text-gray-900">{assignment.title}</h3>
                      <p className="text-sm lg:text-base text-gray-600 mt-1 line-clamp-2">{assignment.description}</p>
                      
                      <div className="flex flex-wrap gap-2 lg:gap-4 mt-3 text-xs lg:text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Due: {formatDate(assignment.due_date || assignment.deadline)}
                        </span>
                        {assignment.mentor_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            From: {assignment.mentor_name}
                          </span>
                        )}
                      </div>

                      {/* Download Assignment File Button */}
                      {assignment.attached_file && (
                        <button
                          onClick={() => handleDownloadAssignment(
                            assignment.attached_file.file_url,
                            assignment.attached_file.filename
                          )}
                          className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Download Assignment ({assignment.attached_file.filename})
                        </button>
                      )}

                      {assignment.submission_notes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">
                            <strong>Your notes:</strong> {assignment.submission_notes}
                          </p>
                        </div>
                      )}

                      {/* Mentor Feedback Display */}
                      {assignment.mentor_feedback && (
                        <div className="mt-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-semibold text-purple-900">Mentor Feedback</span>
                          </div>
                          <p className="text-sm text-purple-800">{assignment.mentor_feedback}</p>
                          {assignment.feedback_at && (
                            <p className="text-xs text-purple-500 mt-2">
                              Received: {formatDate(assignment.feedback_at)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(assignment.status)}`}>
                      {assignment.status}
                    </span>
                    
                    {(assignment.status === 'pending' || assignment.status === 'overdue') && (
                      <Button
                        onClick={() => openSubmitModal(assignment)}
                        className="mt-2"
                        data-testid={`submit-btn-${assignment.id}`}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Submit
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Modal */}
      {showSubmitModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Submit Assignment</h2>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-gray-600 mt-1">{selectedAssignment.title}</p>
            </div>

            <div className="p-6 space-y-4">
              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Submission Notes
                </label>
                <textarea
                  value={submissionNotes}
                  onChange={(e) => setSubmissionNotes(e.target.value)}
                  placeholder="Add notes about your work..."
                  rows={4}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none"
                  data-testid="submission-notes"
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attach File (Optional)
                </label>
                {submissionFile ? (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <Paperclip className="w-5 h-5 text-green-600" />
                    <span className="flex-1 text-green-700">{submissionFile.name}</span>
                    <button
                      onClick={() => setSubmissionFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 p-6 border-2 border-dashed rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors">
                    {uploadingFile ? (
                      <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-gray-400" />
                        <span className="text-gray-600">Click to upload file</span>
                      </>
                    )}
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                      disabled={uploadingFile}
                    />
                  </label>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Max 10MB. Supported: PDF, DOC, DOCX, TXT, JPG, PNG
                </p>
              </div>
            </div>

            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowSubmitModal(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !submissionNotes.trim()}
                data-testid="confirm-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Submit Assignment
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentsPage;
